import { useState, useEffect } from "react";
import { MainLayout } from "../components/Layout";
import { reviewsAPI } from "../lib/api";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import {
  Star, Trash2, MessageSquare, Plus, Eye, EyeOff, ExternalLink,
  Loader2, RefreshCw, Mail, Search, Copy, Check, Info
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const GoogleIcon = () => (
  <svg viewBox="0 0 24 24" className="w-5 h-5">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
);

export default function ReviewsPage() {
  const [internalReviews, setInternalReviews] = useState([]);
  const [googleReviews, setGoogleReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("all");
  const [googlePlaceInfo, setGooglePlaceInfo] = useState(null);
  const [addGoogleDialogOpen, setAddGoogleDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [newGoogleReview, setNewGoogleReview] = useState({ author_name: "", rating: 5, text: "", time: "" });
  const [copiedLink, setCopiedLink] = useState(false);
  const [sendEmailDialogOpen, setSendEmailDialogOpen] = useState(false);
  const [clientsForEmail, setClientsForEmail] = useState([]);
  const [clientsLoading, setClientsLoading] = useState(false);
  const [emailSearch, setEmailSearch] = useState("");
  const [sendingEmail, setSendingEmail] = useState(null);

  useEffect(() => {
    fetchAllReviews();
    fetchGooglePlaceInfo();
  }, []);

  const fetchAllReviews = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const [internalRes, googleRes] = await Promise.all([
        reviewsAPI.list(),
        axios.get(`${API_URL}/api/google-reviews`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setInternalReviews(internalRes.data);
      setGoogleReviews(googleRes.data);
    } catch (err) {
      toast.error("Erreur lors du chargement des avis");
    } finally {
      setLoading(false);
    }
  };

  const fetchGooglePlaceInfo = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/public/google-info`);
      setGooglePlaceInfo(res.data);
    } catch (err) {}
  };

  const fetchClientsForEmail = async () => {
    setClientsLoading(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API_URL}/api/reviews/clients-for-email`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClientsForEmail(res.data);
    } catch (err) {
      toast.error("Erreur lors du chargement des clients");
    } finally {
      setClientsLoading(false);
    }
  };

  const handleOpenSendEmail = () => {
    setSendEmailDialogOpen(true);
    setEmailSearch("");
    fetchClientsForEmail();
  };

  const handleSendEmail = async (clientId, clientName) => {
    setSendingEmail(clientId);
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API_URL}/api/reviews/send-email`, { client_id: clientId }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Email envoyé à ${clientName}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'envoi");
    } finally {
      setSendingEmail(null);
    }
  };

  const handleDeleteInternal = async (id) => {
    if (!window.confirm("Supprimer cet avis ?")) return;
    try {
      await reviewsAPI.delete(id);
      toast.success("Avis supprimé");
      fetchAllReviews();
    } catch (err) {
      toast.error("Erreur");
    }
  };

  const handleDeleteGoogle = async (id) => {
    if (!window.confirm("Supprimer cet avis Google ?")) return;
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API_URL}/api/google-reviews/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Avis supprimé");
      fetchAllReviews();
    } catch (err) {
      toast.error("Erreur");
    }
  };

  const handleToggleVisibility = async (id, currentVisibility) => {
    try {
      const token = localStorage.getItem("token");
      await axios.put(`${API_URL}/api/google-reviews/${id}/visibility`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(currentVisibility ? "Avis masqué" : "Avis visible");
      fetchAllReviews();
    } catch (err) {
      toast.error("Erreur");
    }
  };

  const handleAddGoogleReview = async () => {
    if (!newGoogleReview.author_name) { toast.error("Le nom de l'auteur est requis"); return; }
    setSubmitting(true);
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API_URL}/api/google-reviews`, newGoogleReview, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Avis ajouté");
      setAddGoogleDialogOpen(false);
      setNewGoogleReview({ author_name: "", rating: 5, text: "", time: "" });
      fetchAllReviews();
    } catch (err) {
      toast.error("Erreur lors de l'ajout");
    } finally {
      setSubmitting(false);
    }
  };

  const handleCopyGoogleLink = () => {
    if (googlePlaceInfo?.review_url) {
      navigator.clipboard.writeText(googlePlaceInfo.review_url);
      setCopiedLink(true);
      setTimeout(() => setCopiedLink(false), 2000);
      toast.success("Lien copié !");
    }
  };

  const allReviews = [...internalReviews, ...googleReviews];
  const displayedReviews = activeTab === "all" ? allReviews : activeTab === "internal" ? internalReviews : googleReviews;
  const averageRating = allReviews.length > 0
    ? (allReviews.reduce((sum, r) => sum + r.rating, 0) / allReviews.length).toFixed(1) : 0;
  const ratingDistribution = [5, 4, 3, 2, 1].map(rating => ({
    rating,
    count: allReviews.filter(r => r.rating === rating).length,
    percentage: allReviews.length > 0 ? (allReviews.filter(r => r.rating === rating).length / allReviews.length) * 100 : 0
  }));

  const renderStars = (rating, size = "w-4 h-4") =>
    Array.from({ length: 5 }, (_, i) => (
      <Star key={i} className={`${size} ${i < rating ? "text-amber-500 fill-amber-500" : "text-muted-foreground"}`} />
    ));

  const renderStarInput = (currentRating, onChange) => (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map(star => (
        <button key={star} type="button" onClick={() => onChange(star)} className="focus:outline-none">
          <Star className={`w-6 h-6 transition-colors ${star <= currentRating ? "text-amber-500 fill-amber-500" : "text-muted-foreground hover:text-amber-400"}`} />
        </button>
      ))}
    </div>
  );

  const filteredClients = clientsForEmail.filter(c =>
    c.name.toLowerCase().includes(emailSearch.toLowerCase()) ||
    c.email.toLowerCase().includes(emailSearch.toLowerCase()) ||
    (c.phone || "").includes(emailSearch)
  );

  return (
    <MainLayout>
      <div className="space-y-6" data-testid="reviews-page">

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-chivo text-3xl font-bold text-foreground">Avis clients</h1>
            <p className="text-muted-foreground mt-1">
              {allReviews.length} avis au total · {internalReviews.length} internes · {googleReviews.length} Google
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={fetchAllReviews} disabled={loading}>
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
              Actualiser
            </Button>
            <Button variant="outline" onClick={handleOpenSendEmail}
              className="border-amber-500/30 text-amber-500 hover:bg-amber-500/10">
              <Mail className="w-4 h-4 mr-2" />
              Demander un avis
            </Button>
            <Button onClick={() => setAddGoogleDialogOpen(true)} className="bg-primary">
              <Plus className="w-4 h-4 mr-2" />
              Ajouter avis Google
            </Button>
          </div>
        </div>

        {/* Bandeau lien Google */}
        {googlePlaceInfo && (
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 p-4 rounded-lg bg-blue-500/5 border border-blue-500/20">
            <GoogleIcon />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-foreground">Lien direct pour laisser un avis Google</p>
              <p className="text-xs text-muted-foreground truncate">{googlePlaceInfo.review_url}</p>
            </div>
            <div className="flex gap-2 shrink-0">
              <Button size="sm" variant="outline" onClick={handleCopyGoogleLink} className="border-blue-500/30">
                {copiedLink ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                <span className="ml-1">{copiedLink ? "Copié !" : "Copier"}</span>
              </Button>
              <a href={googlePlaceInfo.review_url} target="_blank" rel="noopener noreferrer">
                <Button size="sm" className="bg-blue-600 hover:bg-blue-700">
                  <ExternalLink className="w-4 h-4 mr-1" />
                  Ouvrir Google
                </Button>
              </a>
            </div>
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
          <Card className="bg-card border-border">
            <div className="p-6 text-center">
              <p className="text-5xl font-light font-chivo text-foreground">{averageRating}</p>
              <div className="flex justify-center gap-1 my-3">{renderStars(Math.round(averageRating))}</div>
              <p className="text-sm text-muted-foreground">Note moyenne</p>
            </div>
          </Card>
          <Card className="bg-card border-border">
            <div className="p-6 text-center">
              <p className="text-4xl font-light font-chivo text-primary">{internalReviews.length}</p>
              <p className="text-sm text-muted-foreground mt-2">Avis internes</p>
            </div>
          </Card>
          <Card className="bg-card border-border border-blue-500/30">
            <div className="p-6 text-center">
              <div className="flex items-center justify-center gap-2 mb-2">
                <GoogleIcon />
                <p className="text-4xl font-light font-chivo text-blue-500">{googleReviews.length}</p>
              </div>
              <p className="text-sm text-muted-foreground">Avis Google</p>
              {googlePlaceInfo && (
                <a href={googlePlaceInfo.maps_url} target="_blank" rel="noopener noreferrer"
                  className="text-xs text-blue-500 hover:underline flex items-center justify-center gap-1 mt-2">
                  <ExternalLink className="w-3 h-3" /> Voir sur Maps
                </a>
              )}
            </div>
          </Card>
          <Card className="bg-card border-border">
            <div className="p-6">
              <p className="font-medium mb-3 text-sm">Distribution</p>
              <div className="space-y-2">
                {ratingDistribution.map(({ rating, count, percentage }) => (
                  <div key={rating} className="flex items-center gap-2">
                    <span className="w-6 text-xs">{rating}★</span>
                    <div className="flex-1 h-2 bg-secondary rounded-full overflow-hidden">
                      <div className="h-full bg-amber-500 rounded-full" style={{ width: `${percentage}%` }} />
                    </div>
                    <span className="w-6 text-xs text-muted-foreground text-right">{count}</span>
                  </div>
                ))}
              </div>
            </div>
          </Card>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 border-b border-border pb-2">
          <Button variant={activeTab === "all" ? "default" : "ghost"} size="sm" onClick={() => setActiveTab("all")}>
            Tous ({allReviews.length})
          </Button>
          <Button variant={activeTab === "internal" ? "default" : "ghost"} size="sm" onClick={() => setActiveTab("internal")}>
            Internes ({internalReviews.length})
          </Button>
          <Button variant={activeTab === "google" ? "default" : "ghost"} size="sm"
            onClick={() => setActiveTab("google")}
            className={activeTab === "google" ? "bg-blue-600" : ""}>
            <GoogleIcon />
            <span className="ml-2">Google ({googleReviews.length})</span>
          </Button>
        </div>

        {/* Liste avis */}
        {loading ? (
          <div className="text-center py-12 text-muted-foreground">
            <Loader2 className="w-8 h-8 mx-auto animate-spin mb-4" />Chargement...
          </div>
        ) : displayedReviews.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <MessageSquare className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>Aucun avis pour le moment</p>
            {activeTab === "google" && (
              <Button className="mt-4" onClick={() => setAddGoogleDialogOpen(true)}>
                <Plus className="w-4 h-4 mr-2" />Ajouter un avis Google
              </Button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {displayedReviews.map((review) => {
              const isGoogle = review.source === "google";
              return (
                <Card key={review.id}
                  className={`bg-card border-border card-hover ${isGoogle ? "border-blue-500/20" : ""} ${!review.is_visible && isGoogle ? "opacity-50" : ""}`}
                  data-testid={`review-card-${review.id}`}>
                  <div className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        {review.profile_photo_url ? (
                          <img src={review.profile_photo_url} alt={review.author_name || review.client_name}
                            className="w-10 h-10 rounded-full object-cover" />
                        ) : (
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${isGoogle ? "bg-blue-500/20" : "bg-primary/20"}`}>
                            <span className={`text-sm font-medium ${isGoogle ? "text-blue-500" : "text-primary"}`}>
                              {(review.author_name || review.client_name || "?")[0]?.toUpperCase()}
                            </span>
                          </div>
                        )}
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-foreground">{review.author_name || review.client_name}</p>
                            {isGoogle && (
                              <span className="bg-blue-500/10 text-blue-500 text-xs px-2 py-0.5 rounded-full">
                                <GoogleIcon />
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground">
                            {review.time || (review.created_at && new Date(review.created_at).toLocaleDateString("fr-FR", {
                              day: "numeric", month: "long", year: "numeric"
                            }))}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        {isGoogle && (
                          <button onClick={() => handleToggleVisibility(review.id, review.is_visible)}
                            className="p-2 hover:bg-secondary rounded-md transition-colors"
                            title={review.is_visible ? "Masquer" : "Afficher"}>
                            {review.is_visible
                              ? <Eye className="w-4 h-4 text-muted-foreground" />
                              : <EyeOff className="w-4 h-4 text-muted-foreground" />}
                          </button>
                        )}
                        <button onClick={() => isGoogle ? handleDeleteGoogle(review.id) : handleDeleteInternal(review.id)}
                          className="p-2 hover:bg-destructive/10 rounded-md transition-colors"
                          data-testid={`delete-review-${review.id}`}>
                          <Trash2 className="w-4 h-4 text-destructive" />
                        </button>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 mb-3">{renderStars(review.rating)}</div>
                    {(review.text || review.comment)
                      ? <p className="text-foreground">{review.text || review.comment}</p>
                      : <p className="text-muted-foreground italic">Aucun commentaire</p>}
                  </div>
                </Card>
              );
            })}
          </div>
        )}

        {/* Dialog: Ajouter avis Google */}
        <Dialog open={addGoogleDialogOpen} onOpenChange={setAddGoogleDialogOpen}>
          <DialogContent className="bg-card border-border max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-chivo flex items-center gap-2">
                <GoogleIcon /> Ajouter un avis Google
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-2">
              <div className="flex gap-3 p-3 rounded-lg bg-blue-500/5 border border-blue-500/20">
                <Info className="w-4 h-4 text-blue-500 shrink-0 mt-0.5" />
                <div className="text-xs text-muted-foreground space-y-1">
                  <p className="font-medium">Comment récupérer vos avis Google :</p>
                  <ol className="list-decimal list-inside space-y-0.5">
                    <li>Ouvrez votre fiche Google Maps</li>
                    <li>Allez dans l'onglet "Avis"</li>
                    <li>Copiez nom, note et texte de chaque avis</li>
                  </ol>
                  {googlePlaceInfo && (
                    <a href={googlePlaceInfo.maps_url} target="_blank" rel="noopener noreferrer"
                      className="flex items-center gap-1 text-blue-500 hover:underline mt-1">
                      <ExternalLink className="w-3 h-3" /> Ouvrir ma fiche Google Maps
                    </a>
                  )}
                </div>
              </div>
              <div className="space-y-2">
                <Label>Nom de l'auteur *</Label>
                <Input value={newGoogleReview.author_name}
                  onChange={(e) => setNewGoogleReview(p => ({ ...p, author_name: e.target.value }))}
                  placeholder="Jean Dupont" className="bg-secondary/50" />
              </div>
              <div className="space-y-2">
                <Label>Note *</Label>
                {renderStarInput(newGoogleReview.rating, (rating) => setNewGoogleReview(p => ({ ...p, rating })))}
              </div>
              <div className="space-y-2">
                <Label>Commentaire</Label>
                <Textarea value={newGoogleReview.text}
                  onChange={(e) => setNewGoogleReview(p => ({ ...p, text: e.target.value }))}
                  placeholder="Excellent service, réparation rapide..." className="bg-secondary/50" rows={3} />
              </div>
              <div className="space-y-2">
                <Label>Date (optionnel)</Label>
                <Input value={newGoogleReview.time}
                  onChange={(e) => setNewGoogleReview(p => ({ ...p, time: e.target.value }))}
                  placeholder="il y a 2 semaines" className="bg-secondary/50" />
              </div>
              <div className="flex gap-3 pt-2">
                <Button variant="outline" onClick={() => setAddGoogleDialogOpen(false)} className="flex-1">Annuler</Button>
                <Button onClick={handleAddGoogleReview} disabled={submitting || !newGoogleReview.author_name}
                  className="flex-1 bg-blue-600 hover:bg-blue-700">
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Ajouter"}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Dialog: Envoyer email d'avis */}
        <Dialog open={sendEmailDialogOpen} onOpenChange={setSendEmailDialogOpen}>
          <DialogContent className="bg-card border-border max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-chivo flex items-center gap-2">
                <Mail className="w-5 h-5 text-amber-500" /> Demander un avis par email
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-2">
              <div className="flex gap-3 p-3 rounded-lg bg-amber-500/5 border border-amber-500/20">
                <Info className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
                <p className="text-xs text-muted-foreground">
                  L'email contient deux boutons : <strong>Laisser un avis Google</strong> (redirige vers ta fiche Google)
                  et <strong>Avis sur la réparation</strong> (page de suivi). Seuls les clients avec email
                  et une réparation livrée apparaissent ici.
                </p>
              </div>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input placeholder="Rechercher par nom, email ou téléphone..."
                  value={emailSearch} onChange={(e) => setEmailSearch(e.target.value)}
                  className="pl-9 bg-secondary/50" />
              </div>
              <div className="max-h-72 overflow-y-auto space-y-2 pr-1">
                {clientsLoading ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Loader2 className="w-6 h-6 mx-auto animate-spin mb-2" />Chargement...
                  </div>
                ) : filteredClients.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground text-sm">
                    {emailSearch ? "Aucun client trouvé" : "Aucun client avec email et réparation livrée"}
                  </div>
                ) : filteredClients.map((client) => (
                  <div key={client.id}
                    className="flex items-center justify-between p-3 rounded-lg bg-secondary/30 hover:bg-secondary/50 transition-colors">
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-sm truncate">{client.name}</p>
                      <p className="text-xs text-muted-foreground truncate">{client.email}</p>
                      {client.last_ticket && (
                        <p className="text-xs text-primary/70">Ticket : {client.last_ticket}</p>
                      )}
                    </div>
                    <Button size="sm" onClick={() => handleSendEmail(client.id, client.name)}
                      disabled={sendingEmail === client.id}
                      className="ml-3 shrink-0 bg-amber-500 hover:bg-amber-600 text-white">
                      {sendingEmail === client.id
                        ? <Loader2 className="w-3 h-3 animate-spin" />
                        : <><Mail className="w-3 h-3 mr-1" />Envoyer</>}
                    </Button>
                  </div>
                ))}
              </div>
              <div className="flex justify-end">
                <Button variant="outline" onClick={() => setSendEmailDialogOpen(false)}>Fermer</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

      </div>
    </MainLayout>
  );
}
